import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, HostListener, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StatsHistoryDialogComponent } from './stats-history-dialog/stats-history-dialog.component';
import { CallCenterStatsService } from '../../../services/call-center-stats.service';
import { SalesStatsService } from '../../../services/sales-stats.service';
import { AuthService } from '../../../services/auth.service';
import { CallCenterStatsData, UpdatePlanRequest, UpdateNotesRequest, UpdateActualRequest as CcUpdateActualRequest } from '../../../interfaces/call-center-stats.interface';
import { SalesStats, UpdatePlanRequest as SalesUpdatePlanRequest, UpdateNotesRequest as SalesUpdateNotesRequest, UpdateActualRequest } from '../../../interfaces/sales-stats.interface';
import { DateService } from '../../../services/date.service';
import { SmallInputMenuComponent } from '../../../shared/components/small-input-menu/small-input-menu.component';
import { StatisticsDataLoaderService } from '../../../services/statistics-data-loader.service';
import { StatisticsSummaryService } from '../../../services/statistics-summary.service';
import { EditingManagerService } from '../../../services/editing-manager.service';
import { NotesManagerService } from '../../../services/notes-manager.service';
import { StatisticsFormatterService } from '../../../services/statistics-formatter.service';
import { PerformanceMetric } from '../../../interfaces/statistics.interface';
import { TableStateManagerService } from '../../../services/table-state-manager.service';
import { User } from '../../../interfaces/auth.interface';
import { SummaryNotesService } from '../../../services/summary-notes.service';

@Component({
  selector: 'app-statistics-table',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatChipsModule,
    MatTooltipModule,
    SmallInputMenuComponent
],
  providers: [EditingManagerService, NotesManagerService, StatisticsFormatterService, TableStateManagerService], // Minden komponens saját service instance-okat kap
  templateUrl: './statistics-table.html',
  styleUrls: ['./statistics-table.scss']
})
export class StatisticsTableComponent implements OnInit, OnChanges {
  @Input() month: string = 'június';
  @Input() year: number = 2024;
  @Input() metrics: PerformanceMetric[] = [];
  @Input() daysInMonth: number = 30;
  @Input() colleagueName: string = 'Andrea';
  @Input() isKumMode: boolean = false;
  @Input() userId: number = 0; // Add userId input
  @Input() isSummaryTable: boolean = false; // Support for summary table mode
  @Input() allColleaguesWithMetrics: {colleague: any, performanceMetrics: PerformanceMetric[]}[] = []; // All colleagues data for summary
  @Input() canEditPlan: boolean = false; // Jogosultság a Plan sorok szerkesztéséhez
  @Input() preloadedStats: CallCenterStatsData[] | null = null; // Előre betöltött CC statisztikák (bulk endpoint-ból)
  @Input() preloadedKumStats: SalesStats[] | null = null; // Előre betöltött KÜM statisztikák (bulk endpoint-ból)
  @Input() preloadedAllStatsMap: { [userId: number]: CallCenterStatsData[] } | null = null; // Summary tábla: összes kolléga preloaded CC adatai
  @Input() preloadedAllKumStatsMap: { [userId: number]: SalesStats[] } | null = null; // Summary tábla: összes kolléga preloaded KÜM adatai
  @Input() otherColleagues: User[] = []; // Többi azonos módú kolléga - tervszámok másolása forrásként

  // Output event to notify parent when plan data is saved
  @Output() planDataSaved = new EventEmitter<{userId: number, weekNumber: number, year: number}>();
  // Tervszámok másolásának kérése egy másik kollégától
  @Output() copyPlanRequested = new EventEmitter<{ sourceUserId: number, sourceUsername: string }>();

  dayNumbers: number[] = [];
  weekNumbers: number[] = [];
  weekGroups: {weekNumber: number, startDay: number, colspan: number, isWeekend: boolean}[] = [];

  // Summary loading state
  private isLoadingSummary: boolean = false;
  private summaryTimeout: any = null;

  // Napi jegyzetek (egyéni táblázatokhoz – NotesManagerService / TableStateManagerService)
  @ViewChild('dayNoteMenu') dayNoteMenu!: SmallInputMenuComponent;

  // Összesítő táblázat napi notejai (külön service + állapot)
  summaryNotes: { [dayIndex: number]: string } = {};
  @ViewChild('summaryDayNoteMenu') summaryDayNoteMenu!: SmallInputMenuComponent;
  
  // A hét napjainak nevû konstansok
  private readonly DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  /** Megnyitott módosítási napló dialog referenciája (singleton) */
  private historyDialogRef: MatDialogRef<StatsHistoryDialogComponent> | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private snackBar: MatSnackBar,
    private dataLoaderService: StatisticsDataLoaderService,
    private summaryService: StatisticsSummaryService,
    private editingManager: EditingManagerService,
    private notesManager: NotesManagerService,
    private dateService: DateService,
    private formatter: StatisticsFormatterService,
    private tableState: TableStateManagerService,
    private authService: AuthService,
    private dialog: MatDialog,
    private summaryNotesService: SummaryNotesService
  ) {}

  /**
   * Helper method to get the effective year considering debug mode
   */
  private getEffectiveYear(): number {
    const currentDate = this.dateService.getCurrentDate();
    const isDebugMode = this.dateService.isDebugMode();
    return isDebugMode ? currentDate.getFullYear() : this.year;
  }

  // Az ngOnChanges már betöltötte-e az adatokat az első rendereléskor
  private dataLoadedByChanges = false;

  ngOnInit() {    
    this.generateDayNumbers();
    this.generateWeekNumbers();
    this.generateWeekGroups();

    // Ha ngOnChanges már kezelte az adatbetöltést, ne töltsük újra
    if (this.dataLoadedByChanges) {
      return;
    }
    
    if (this.isSummaryTable) {
      // Késleltetjük a summary generálást, hogy elkerüljük az ExpressionChangedAfterItHasBeenCheckedError hibát
      setTimeout(() => {
        this.generateSummaryMetrics();
      }, 0);
      // Összesítő tábla notejai betöltése
      this.loadSummaryNotes();
    } else if (this.preloadedKumStats !== null && this.preloadedKumStats !== undefined && this.isKumMode) {
      // KÜM bulk-ból előre betöltött adatok használata
      this.usePreloadedKumStatsData(this.preloadedKumStats);
      this.loadNotesFromPreloadedKumStats();
    } else if (this.preloadedStats !== null && this.preloadedStats !== undefined) {
      // CC bulk-ból előre betöltött adatok használata
      this.usePreloadedStatsData(this.preloadedStats);
      this.loadNotesFromPreloadedStats();
    } else {
      this.loadStatsFromDatabase();
      // Jegyzetek betöltése is
      this.loadNotesFromDatabase();
    }
  }

  ngOnChanges(changes: SimpleChanges) {    
    // Reagálás a month, year, vagy daysInMonth változásokra
    if (changes['month'] || changes['year'] || changes['daysInMonth']) {
      this.generateDayNumbers();
      this.generateWeekNumbers();
      this.generateWeekGroups();
      
    if (changes['daysInMonth'] && this.metrics.length > 0) {
        this.ensureCorrectArrayLengths();
      }
    }
    
    // Ha isSummaryTable módban vagyunk és allColleaguesWithMetrics változik
    if (this.isSummaryTable) {
      if (changes['allColleaguesWithMetrics'] || changes['month'] || changes['year'] || changes['daysInMonth'] || changes['preloadedAllStatsMap'] || changes['preloadedAllKumStatsMap']) {
        // Debounce the summary generation to prevent multiple rapid calls
        if (this.summaryTimeout) {
          clearTimeout(this.summaryTimeout);
        }
        
        this.summaryTimeout = setTimeout(() => {
          this.generateSummaryMetrics();
        }, 100); // 100ms debounce
      }
      // Összesítő tábla notejai újratöltése hónap / év / módváltáskor
      if (changes['month'] || changes['year'] || changes['isKumMode']) {
        this.loadSummaryNotes();
      }
    }
    // Ha preloadedKumStats változik (KÜM bulk betöltés kész vagy új hónap)
    else if (changes['preloadedKumStats'] && this.isKumMode) {
      if (this.preloadedKumStats !== null && this.preloadedKumStats !== undefined) {
        this.usePreloadedKumStatsData(this.preloadedKumStats);
        this.notesManager.clearNotes();
        this.loadNotesFromPreloadedKumStats();
        this.dataLoadedByChanges = true;
      }
    }
    // Ha preloadedStats változik (CC bulk betöltés kész vagy új hónap)
    else if (changes['preloadedStats'] && !this.isKumMode) {
      if (this.preloadedStats !== null && this.preloadedStats !== undefined) {
        this.usePreloadedStatsData(this.preloadedStats);
        // Jegyzetek betöltése a preloaded adatokból (nincs extra API hívás)
        this.notesManager.clearNotes();
        this.loadNotesFromPreloadedStats();
        this.dataLoadedByChanges = true;
      }
      // Ha null: betöltés folyamatban, várunk az adatra (nem self-load-olunk)
    }
    // Ha userId, month vagy year változik, újratöltjük az adatokat (csak ha nem summary table és nincs preloaded)
    else if (changes['userId'] || changes['month'] || changes['year']) {
      const hasPreloaded = this.isKumMode
        ? (this.preloadedKumStats !== null && this.preloadedKumStats !== undefined)
        : (this.preloadedStats !== null && this.preloadedStats !== undefined);
      if (!hasPreloaded) {
        this.loadStatsFromDatabase();
      }
      // Jegyzetek újratöltése amikor hónap vagy év változik — csak ha nincs preloaded mód
      if (changes['month'] || changes['year']) {
        this.notesManager.clearNotes();
        // Preloaded módban a jegyzetek a preloaded data változásakor töltődnek be
        if (!hasPreloaded) {
          this.loadNotesFromDatabase();
        }
      }
    }
  }

  private generateDayNumbers() {
    this.dayNumbers = this.dateService.generateDayNumbers(this.daysInMonth);
  }

  private generateWeekNumbers() {
    const effectiveYear = this.getEffectiveYear();
    this.weekNumbers = this.dateService.generateWeekNumbers(
      effectiveYear,
      this.getMonthIndex(),
      this.daysInMonth
    );
  }

  private generateWeekGroups() {
    const effectiveYear = this.getEffectiveYear();
    this.weekGroups = this.dateService.generateWeekGroups(
      this.weekNumbers,
      this.daysInMonth,
      effectiveYear,
      this.getMonthIndex()
    );
  }



  getActualSum(metric: PerformanceMetric): number {
    return this.formatter.getActualSum(metric);
  }

  getPlanSum(metric: PerformanceMetric): number {
    return this.formatter.getPlanSum(metric);
  }

  getPercentageAverage(metric: PerformanceMetric): number {
    return this.formatter.getAveragePercentage(metric);
  }

  getCellClass(rowType: string, dayIndex: number, value: number): string {
    return this.formatter.getCellClass(rowType, dayIndex, value, this.year, this.getMonthIndex());
  }

  /**
   * Cell class oszlop jegyzet figyelembevételével
   */
  getCellClassWithNote(rowType: string, dayIndex: number, value: number): string {
    const hasNote = this.isSummaryTable
      ? this.hasSummaryNoteForDay(dayIndex)
      : this.hasNoteForDay(dayIndex);
    const noteColor = hasNote ? this.getNoteColor(dayIndex) : 'orange';
    return this.formatter.getCellClassWithNote(
      rowType, 
      dayIndex, 
      value, 
      this.year, 
      this.getMonthIndex(),
      hasNote,
      noteColor
    );
  }

  /** Napi note szövegének kinyerése (szín prefix nélkül) */
  getNoteText(dayIndex: number): string {
    const raw = this.isSummaryTable
      ? this.getSummaryNoteForDay(dayIndex)
      : (this.notes[dayIndex] || '');
    return raw.startsWith('red:') ? raw.substring(4) : raw;
  }

  /** Napi note színének meghatározása a tárolt érték alapján */
  getNoteColor(dayIndex: number): 'orange' | 'red' {
    const raw = this.isSummaryTable
      ? this.getSummaryNoteForDay(dayIndex)
      : (this.notes[dayIndex] || '');
    return raw.startsWith('red:') ? 'red' : 'orange';
  }

  /** Note chip CSS osztálya a szín alapján */
  getNoteChipClass(dayIndex: number): string {
    return this.getNoteColor(dayIndex) === 'red' ? 'note-chip-red' : '';
  }


  isEditableCell(metricIndex: number, rowType: 'actual' | 'plan'): boolean {
    return this.editingManager.isEditableCell(metricIndex, rowType, this.isKumMode, this.metrics);
  }

  isCellEditableForDay(metricIndex: number, rowType: 'actual' | 'plan', dayIndex: number): boolean {
    return this.editingManager.isCellEditableForDay(
      metricIndex, 
      rowType, 
      dayIndex, 
      this.isKumMode, 
      this.metrics, 
      (dayIdx) => this.isWeekend(dayIdx)
    );
  }

  private getMonthIndex(): number {
    return this.dateService.getMonthIndex(this.month);
  }

  formatPercentage(value: number, dayIndex?: number): string {
    return this.formatter.formatPercentage(value, dayIndex, this.year, this.getMonthIndex());
  }

  formatValue(value: number, dayIndex?: number): string {
    return this.formatter.formatValue(value, dayIndex, this.year, this.getMonthIndex());
  }

  isWeekend(dayIndex: number): boolean {
    return this.dateService.isWeekend(dayIndex, this.year, this.getMonthIndex());
  }

  // Editing functionality
  onRowDoubleClick(metricIndex: number, rowType: 'actual' | 'plan', dayIndex: number) {
    // Ha már szerkesztés alatt áll egy hét, először mentsük el
    if (this.editingManager.currentEditingWeek) {
      this.saveEditing();
    }
    
    // Próbáljuk meg elindítani a hét szerkesztését
    const success = this.editingManager.startWeekEditing(
      metricIndex,
      rowType,
      dayIndex,
      this.isKumMode,
      this.metrics,
      this.weekNumbers,
      (dayIdx) => this.isWeekend(dayIdx)
    );
    
    if (success) {
      // ✅ Async change detection hogy elkerüljük az ExpressionChangedAfterItHasBeenCheckedError hibát
      setTimeout(() => {
        this.cdr.detectChanges();
        const firstInput = document.querySelector('.edit-input') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
          firstInput.select();
        }
      }, 0);
    }
  }

  isEditing(metricIndex: number, rowType: 'actual' | 'plan', dayIndex: number): boolean {
    return this.editingManager.isEditing(metricIndex, rowType, dayIndex, (dayIdx) => this.isWeekend(dayIdx));
  }

  getEditingValueIndex(dayIndex: number): number {
    return this.editingManager.getEditingValueIndex(dayIndex);
  }

  getEditingValue(metricIndex: number, dayIndex: number): number {
    return this.editingManager.getEditingValue(metricIndex, dayIndex);
  }

  updateEditingValue(metricIndex: number, dayIndex: number, value: number) {
    this.editingManager.updateEditingValue(metricIndex, dayIndex, value);
  }

  // Single cell editing methods for actual values
  onCellDoubleClick(metricIndex: number, rowType: 'actual' | 'plan', dayIndex: number) {
    // Lemondunk minden jelenlegi szerkesztést
    this.editingManager.cancelAllEditing();

    // Próbáljuk meg elindítani az egyes cella szerkesztését
    const success = this.editingManager.startCellEditing(
      metricIndex,
      rowType,
      dayIndex,
      this.isKumMode,
      this.metrics,
      (dayIdx) => this.isWeekend(dayIdx)
    );

    if (success) {
      // Focus the input after a brief delay
      setTimeout(() => {
        this.cdr.detectChanges();
        const input = document.querySelector('.edit-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 0);
    }
  }

  getSingleCellEditingValue(metricIndex: number, dayIndex: number): number {
    return this.editingManager.getSingleCellEditingValue(metricIndex, dayIndex, this.metrics);
  }

  updateSingleCellEditingValue(metricIndex: number, dayIndex: number, value: number) {
    this.editingManager.updateSingleCellEditingValue(metricIndex, dayIndex, value);
  }

  saveSingleCellEditing() {
    const editingCell = this.editingManager.currentEditingCell;
    if (!editingCell || !this.userId) return;

    const { metricIndex, dayIndex } = editingCell;
    const actualYear = this.getEffectiveYear();
    const currentValue = this.editingManager.currentSingleCellEditingValue;

    // Ha az érték nem változott, csak kilépünk szerkesztő módból
    const originalValue = this.metrics[metricIndex].actualValues[dayIndex] || 0;
    if (currentValue === originalValue) {
      this.cancelSingleCellEditing();
      return;
    }

    // Update local value
    this.metrics[metricIndex].actualValues[dayIndex] = currentValue;
    this.updatePercentages(metricIndex);

    // Determine which sales metric this corresponds to
    const metricName = this.metrics[metricIndex].name;
    const date = new Date(actualYear, this.getMonthNumber() - 1, dayIndex + 1);
    const weekNumber = this.dateService.getWeekNumber(date);
    const dayOfWeek = date.getDay();

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const weekDayIndex = dayOfWeek - 1; // 0=Hétfő, 4=Péntek

      // Map metric names to request fields
      const actualRequest: any = {
        user_id: this.userId,
        week_number: weekNumber,
        year: actualYear,
        day_index: weekDayIndex
      };

      if (this.isKumMode) {
        // KÜM metrikák mentése SalesStatsService-szel
        if (metricName === 'Egyeztetett időpont') {
          actualRequest.appointment_scheduled_actual = currentValue;
        } else if (metricName === 'Vagyonra egyeztetett') {
          actualRequest.property_insurance_appointment_actual = currentValue;
        } else if (metricName === 'Sikeres első tárgyalás') {
          actualRequest.succesfulfirstmeetings_actual = currentValue;
        } else if (metricName === 'Sikeres felmérés') {
          actualRequest.successfulassessments_actual = currentValue;
        } else if (metricName === 'Sikeres ajánlat prezentáció') {
          actualRequest.successfulproposalpresentations_actual = currentValue;
        } else if (metricName === 'Sikeres zárás') {
          actualRequest.successfulclosures_actual = currentValue;
        }

        this.salesStatsService.logActualValue(actualRequest).subscribe({
          next: (response) => {
            this.snackBar.open(`✅ ${metricName} tény érték sikeresen mentve!`, 'Bezár', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            });
            this.loadStatsFromDatabase();
          },
          error: (error) => {
            this.snackBar.open(`❌ Hiba történt a ${metricName} tény érték mentése során!`, 'Bezár', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      } else {
        // CC metrikák mentése CallCenterStatsService-szel
        const ccActualRequest: CcUpdateActualRequest = {
          user_id: this.userId,
          week_number: weekNumber,
          year: actualYear,
          day_index: weekDayIndex
        };

        if (metricName === 'Indított hívás') {
          ccActualRequest.initiated_calls_actual = currentValue;
        } else if (metricName === 'Egyeztetett időpont') {
          ccActualRequest.appointment_scheduled_actual = currentValue;
        } else if (metricName === 'Vagyonra egyeztetett') {
          ccActualRequest.property_insurance_appointment_actual = currentValue;
        }

        this.callCenterStatsService.updateActualValue(ccActualRequest).subscribe({
          next: (response) => {
            this.snackBar.open(`✅ ${metricName} tény érték sikeresen mentve!`, 'Bezár', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            });
            this.loadStatsFromDatabase();
          },
          error: (error) => {
            this.snackBar.open(`❌ Hiba történt a ${metricName} tény érték mentése során!`, 'Bezár', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    }

    this.cancelSingleCellEditing();
  }

  cancelSingleCellEditing() {
    this.editingManager.cancelCellEditing();
    this.cdr.detectChanges();
  }

  saveEditing() {
    const editingWeek = this.editingManager.currentEditingWeek;
    const editingDayIndices = this.editingManager.currentEditingDayIndices;
    const editingValues = this.editingManager.currentEditingValues;
    
    if (!editingWeek || !this.userId) return;
    
    const { metricIndices, weekNumber } = editingWeek;
    const actualYear = this.getEffectiveYear();
    
    // Update the plan values for all edited metrics locally
    metricIndices.forEach((metricIndex: number) => {
      editingDayIndices.forEach((dayIndex: number, editIndex: number) => {
        this.metrics[metricIndex].planValues[dayIndex] = editingValues[metricIndex][editIndex] || 0;
      });
      
      // Recalculate percentages after editing plan values
      this.updatePercentages(metricIndex);
    });
    
    if (this.isKumMode) {
      // KÜM módban SalesStatsService használata
      this.saveKumPlanValues(weekNumber, actualYear, metricIndices, editingDayIndices);
    } else {
      // Call Center módban CallCenterStatsService használata
      this.saveCallCenterPlanValues(weekNumber, actualYear, editingDayIndices);
    }
  }
  
  private saveKumPlanValues(weekNumber: number, actualYear: number, metricIndices: number[], editingDayIndices: number[]) {
    // KÜM metrikák feltérképezése
    const appointmentIndex = this.metrics.findIndex(m => m.name === 'Egyeztetett időpont');
    const propertyInsuranceKumIndex = this.metrics.findIndex(m => m.name === 'Vagyonra egyeztetett');
    const firstMeetingIndex = this.metrics.findIndex(m => m.name === 'Sikeres első tárgyalás');
    const assessmentIndex = this.metrics.findIndex(m => m.name === 'Sikeres felmérés');
    const proposalIndex = this.metrics.findIndex(m => m.name === 'Sikeres ajánlat prezentáció');
    const closureIndex = this.metrics.findIndex(m => m.name === 'Sikeres zárás');
    
    // KÜM plan értékek készítése (Hétfő-Péntek)
    const appointment_scheduled_plan = [0, 0, 0, 0, 0];
    const property_insurance_appointment_plan = [0, 0, 0, 0, 0];
    const succesfulfirstmeetings_plan = [0, 0, 0, 0, 0];
    const successfulassessments_plan = [0, 0, 0, 0, 0];
    const successfulproposalpresentations_plan = [0, 0, 0, 0, 0];
    const successfulclosures_plan = [0, 0, 0, 0, 0];
    
    // Gyűjtjük a ténylegesen szerkesztett hét-pozíciókat (tört hét kezeléséhez)
    const weekDayIndices: number[] = [];
    
    // Meghatározzuk a hét napjait a hónapban
    editingDayIndices.forEach((dayIndex: number) => {
      const date = new Date(actualYear, this.getMonthNumber() - 1, dayIndex + 1);
      const dayOfWeek = date.getDay(); // 0=Vasárnap, 1=Hétfő, ..., 6=Szombat
      
      // Konvertálás: 1=Hétfő=0, 2=Kedd=1, ..., 5=Péntek=4
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const weekDayIndex = dayOfWeek - 1; // 1-5 -> 0-4
        weekDayIndices.push(weekDayIndex); // tört hét: csak ezeket a pozíciókat frissítjük
        
        if (appointmentIndex !== -1) {
          appointment_scheduled_plan[weekDayIndex] = this.metrics[appointmentIndex].planValues[dayIndex];
        }
        if (propertyInsuranceKumIndex !== -1) {
          property_insurance_appointment_plan[weekDayIndex] = this.metrics[propertyInsuranceKumIndex].planValues[dayIndex];
        }
        if (firstMeetingIndex !== -1) {
          succesfulfirstmeetings_plan[weekDayIndex] = this.metrics[firstMeetingIndex].planValues[dayIndex];
        }
        if (assessmentIndex !== -1) {
          successfulassessments_plan[weekDayIndex] = this.metrics[assessmentIndex].planValues[dayIndex];
        }
        if (proposalIndex !== -1) {
          successfulproposalpresentations_plan[weekDayIndex] = this.metrics[proposalIndex].planValues[dayIndex];
        }
        if (closureIndex !== -1) {
          successfulclosures_plan[weekDayIndex] = this.metrics[closureIndex].planValues[dayIndex];
        }
      }
    });
    
    const salesUpdateRequest: SalesUpdatePlanRequest = {
      user_id: this.userId,
      week_number: weekNumber,
      year: actualYear,
      appointment_scheduled_plan,
      property_insurance_appointment_plan,
      succesfulfirstmeetings_plan,
      successfulassessments_plan,
      successfulproposalpresentations_plan,
      successfulclosures_plan,
      day_indices: weekDayIndices
    };
    
    // KÜM API hívás
    this.salesStatsService.updatePlanValues(salesUpdateRequest).subscribe({
      next: (response) => {
        this.snackBar.open(`✅ ${weekNumber}. hét KÜM adatai sikeresen mentve!`, 'Bezár', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        
        // Újratöltjük az adatokat az adatbázisból hogy a friss értékeket lássuk
        this.loadStatsFromDatabase();
        
        this.planDataSaved.emit({
          userId: this.userId,
          weekNumber: weekNumber,
          year: actualYear
        });
        
        this.cancelEditing();
      },
      error: (error) => {
        this.snackBar.open(`❌ Hiba történt a ${weekNumber}. hét KÜM mentése során!`, 'Bezár', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        
        this.cancelEditing();
      }
    });
  }
  
  private saveCallCenterPlanValues(weekNumber: number, actualYear: number, editingDayIndices: number[]) {
    // Call Center metrikák keresése
    const initatedCallsIndex = this.metrics.findIndex(m => m.name === 'Indított hívás');
    const appointmentIndex = this.metrics.findIndex(m => m.name === 'Egyeztetett időpont');
    const propertyInsuranceIndex = this.metrics.findIndex(m => m.name === 'Vagyonra egyeztetett');
    
    if (initatedCallsIndex === -1 || appointmentIndex === -1) {
      return;
    }
    
    // Call Center plan értékek készítése (Hétfő-Péntek)
    const initiated_calls_plan = [0, 0, 0, 0, 0];
    const appointment_scheduled_plan = [0, 0, 0, 0, 0];
    const property_insurance_appointment_plan = [0, 0, 0, 0, 0];
    
    // Gyűjtjük a ténylegesen szerkesztett hét-pozíciókat (tört hét kezeléséhez)
    const weekDayIndices: number[] = [];
    
    // Meghatározzuk a hét napjait a hónapban
    editingDayIndices.forEach((dayIndex: number) => {
      const date = new Date(actualYear, this.getMonthNumber() - 1, dayIndex + 1);
      const dayOfWeek = date.getDay(); // 0=Vasárnap, 1=Hétfő, ..., 6=Szombat
      
      // Konvertálás: 1=Hétfő=0, 2=Kedd=1, ..., 5=Péntek=4
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const weekDayIndex = dayOfWeek - 1; // 1-5 -> 0-4
        weekDayIndices.push(weekDayIndex); // tört hét: csak ezeket a pozíciókat frissítjük
        initiated_calls_plan[weekDayIndex] = this.metrics[initatedCallsIndex].planValues[dayIndex];
        appointment_scheduled_plan[weekDayIndex] = this.metrics[appointmentIndex].planValues[dayIndex];
        
        // Ha van Vagyonra egyeztetett metrika, akkor azt is mentjük
        if (propertyInsuranceIndex !== -1) {
          property_insurance_appointment_plan[weekDayIndex] = this.metrics[propertyInsuranceIndex].planValues[dayIndex];
        }
      }
    });
    
    const ccUpdateRequest = {
      user_id: this.userId,
      week_number: weekNumber,
      year: actualYear,
      initiated_calls_plan: initiated_calls_plan,
      appointment_scheduled_plan: appointment_scheduled_plan,
      property_insurance_appointment_plan: property_insurance_appointment_plan,
      day_indices: weekDayIndices
    };
    
    // Call Center API hívás
    this.callCenterStatsService.updatePlanValues(ccUpdateRequest).subscribe({
      next: (response) => {
        this.snackBar.open(`✅ ${weekNumber}. hét adatai sikeresen mentve!`, 'Bezár', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        
        // Újratöltjük az adatokat az adatbázisból hogy a friss értékeket lássuk
        this.loadStatsFromDatabase();
        
        this.planDataSaved.emit({
          userId: this.userId,
          weekNumber: weekNumber,
          year: actualYear
        });
        
        this.cancelEditing();
      },
      error: (error) => {
        this.snackBar.open(`❌ Hiba történt a ${weekNumber}. hét mentése során!`, 'Bezár', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        
        this.cancelEditing();
      }
    });
  }

  cancelEditing() {
    this.editingManager.cancelWeekEditing();
    this.cdr.detectChanges();
  }

  private updatePercentages(metricIndex: number) {
    this.formatter.updatePercentages(this.metrics[metricIndex], this.daysInMonth);
  }

  /**
   * Biztosítja, hogy minden metric tömb a helyes hosszúságú legyen (daysInMonth)
   */
  private ensureCorrectArrayLengths() {
    this.formatter.ensureCorrectArrayLengths(this.metrics, this.daysInMonth);
    
    // Százalékok újraszámítása a végén
    this.formatter.updateAllPercentages(this.metrics, this.daysInMonth);
  }

  /**
   * Adatbázis adatok betöltése a havi táblázathoz
   */
  private loadStatsFromDatabase(): void {
    this.dataLoaderService.loadStatsFromDatabase(
      this.userId,
      this.isKumMode,
      this.month,
      this.metrics,
      this.daysInMonth,
      () => this.initializeDefaultMetrics(),
      (weeklyStats) => this.populateMetricsFromSalesStats(weeklyStats),
      (weeklyStats) => this.populateMetricsFromWeeklyStats(weeklyStats),
      this.snackBar,
      this.cdr
    );
  }

  /**
   * Előre betöltött (bulk) statisztikai adatok felhasználása API hívás helyett.
   * KÜM módban fallback a normál betöltésre.
   */
  private usePreloadedStatsData(stats: CallCenterStatsData[]): void {
    if (this.isKumMode) {
      // KÜM mód más adatformátumot használ, nem tudja kezelni a CC preloaded adatokat
      this.loadStatsFromDatabase();
      return;
    }
    if (stats.length === 0) {
      this.initializeDefaultMetrics();
    } else {
      // FONTOS: Inicializáljuk a metrikákat MIELŐTT átadjuk a data loader service-nek,
      // hogy ne legyen stale reference a metrics tömbre
      if (this.metrics.length === 0) {
        this.initializeDefaultMetrics();
      }
      this.populateMetricsFromWeeklyStats(stats);
    }
    this.cdr.detectChanges();
  }

  /**
   * Előre betöltött KÜM (bulk) statisztikai adatok felhasználása API hívás helyett.
   */
  private usePreloadedKumStatsData(stats: SalesStats[]): void {
    if (stats.length === 0) {
      this.initializeDefaultMetrics();
    } else {
      // FONTOS: Inicializáljuk a metrikákat MIELŐTT átadjuk a data loader service-nek,
      // hogy ne legyen stale reference a metrics tömbre
      if (this.metrics.length === 0) {
        this.initializeDefaultMetrics();
      }
      this.populateMetricsFromSalesStats(stats);
    }
    this.cdr.detectChanges();
  }

  /**
   * Hónap név alapján szám meghatározása (1-12)
   */
  private getMonthNumber(): number {
    return this.dateService.getMonthNumber(this.month);
  }

  /**
   * Sales heti adatok alapján feltölti a havi táblázat metrikáit
   */
  private populateMetricsFromSalesStats(weeklyStats: SalesStats[]): void {
    // Biztosítjuk, hogy a metrikák inicializálva legyenek mielőtt a data loader elkezdi feltölteni
    if (this.metrics.length === 0) {
      this.initializeDefaultMetrics();
    }
    this.dataLoaderService.populateMetricsFromSalesStats(
      weeklyStats,
      this.metrics,
      this.daysInMonth,
      this.weekNumbers,
      () => this.getMonthNumber(),
      (metricIndex) => this.updatePercentages(metricIndex),
      () => this.initializeDefaultMetrics()
    );
  }

  /**
   * Call Center heti adatok alapján feltölti a havi táblázat metrikáit
   */
  private populateMetricsFromWeeklyStats(weeklyStats: CallCenterStatsData[]): void {
    // Biztosítjuk, hogy a metrikák inicializálva legyenek mielőtt a data loader elkezdi feltölteni
    if (this.metrics.length === 0) {
      this.initializeDefaultMetrics();
    }
    this.dataLoaderService.populateMetricsFromWeeklyStats(
      weeklyStats,
      this.metrics,
      this.daysInMonth,
      this.weekNumbers,
      () => this.getMonthNumber(),
      (metricIndex) => this.updatePercentages(metricIndex),
      () => this.initializeDefaultMetrics()
    );
  }

  /**
   * Alapértelmezett metrikák inicializálása ha üresek
   */
  private initializeDefaultMetrics(): void {
    this.metrics = this.summaryService.initializeDefaultMetrics(this.isKumMode, this.daysInMonth);
  }

    // Global click listener to handle clicking outside editing area
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.editingManager.currentEditingWeek) return;

    const target = event.target as HTMLElement;
    
    // Check if the click was inside an edit input or on an editable cell
    if (!target.closest('.editable-cell') && !target.classList.contains('edit-input')) {
      this.cancelEditing();
    }
  }

  /**
   * Generate summary metrics by loading and summing all colleagues' data from database
   */
  private generateSummaryMetrics(): void {
     // Prevent multiple simultaneous loads
    if (this.isLoadingSummary) {
      return;
    }
    
    this.isLoadingSummary = true;
    
    if (!this.allColleaguesWithMetrics || this.allColleaguesWithMetrics.length === 0) {
      this.metrics = [];
      this.isLoadingSummary = false;
      return;
    }

    this.summaryService.generateSummaryMetrics(
      this.isKumMode,
      this.allColleaguesWithMetrics,
      this.daysInMonth,
      this.year,
      () => this.getMonthNumber(),
      this.weekNumbers,
      this.snackBar,
      this.cdr,
      this.preloadedAllStatsMap || undefined,
      this.preloadedAllKumStatsMap || undefined
    ).then((metrics) => {
      this.metrics = metrics;
      this.isLoadingSummary = false;
      this.cdr.markForCheck();
    }).catch((error) => {
      this.metrics = [];
      this.isLoadingSummary = false;
      this.cdr.markForCheck();
    });
  }

  
  
  /**
   * ✅ Public method to refresh summary metrics from database
   * Called by parent wrapper when individual tables update plan data
   */
  public refreshSummaryMetrics(): void {
    if (this.isSummaryTable) {
      // Reset loading flag to ensure fresh load
      this.isLoadingSummary = false;
      this.generateSummaryMetrics();
    }
  }

  /**
   * Napra való hover kezelés
   */
  onDayHover(dayIndex: number): void {
    this.tableState.onDayHover(dayIndex);
  }

  /**
   * Napról való leave kezelés  
   */
  onDayLeave(): void {
    this.tableState.onDayLeave();
  }

  /**
   * Napra való kattintás - jegyzet menü megnyitás
   */
  setSelectedDay(dayIndex: number, trigger: any): void {
    this.tableState.setSelectedDay(dayIndex, trigger);
  }

  /**
   * Menü akció befejezés
   */
  onMenuAction(): void {
    this.tableState.onMenuAction();
  }

  /**
   * Jegyzet ikon megjelenítésének feltétele
   */
  shouldShowNoteIcon(dayIndex: number): boolean {
    return this.tableState.shouldShowNoteIcon(
      dayIndex,
      this.hasNoteForDay(dayIndex),
      this.isSummaryTable,
      this.isWeekend(dayIndex)
    );
  }

  /**
   * Jegyzet chip megjelenítésének feltétele
   */
  shouldShowNoteChip(dayIndex: number): boolean {
    return this.tableState.shouldShowNoteChip(
      dayIndex,
      this.hasNoteForDay(dayIndex),
      this.isSummaryTable
    );
  }

  /**
   * Van-e jegyzet az adott naphoz
   */
  hasNoteForDay(dayIndex: number): boolean {
    return this.notesManager.hasNoteForDay(dayIndex);
  }

  /**
   * Template-hez való notes objektum getter
   */
  get notes(): { [dayIndex: number]: string } {
    return this.notesManager.currentNotes;
  }

  /**
   * Template-hez való selectedDay getter
   */
  get selectedDay(): number {
    return this.tableState.currentSelectedDay;
  }

  /**
   * Jelenlegi menü trigger getter (SmallInputMenuComponent bezárásához)
   */
  get currentMenuTrigger(): any {
    return this.tableState.currentMenuTrigger;
  }

  /**
   * Új jegyzet hozzáadása
   */
  onNoteAdded(note: string, dayIndex: number): void {
    this.notesManager.addNote(dayIndex, note);
    this.onMenuAction();
    this.saveNotesToDatabase(dayIndex);
  }

  /**
   * Jegyzet törlése
   */
  onNoteDeleted(dayIndex: number): void {
    this.notesManager.deleteNote(dayIndex);
    this.onMenuAction();
    this.saveNotesToDatabase(dayIndex, true);
  }

  /**
   * Jegyzet lemondása
   */
  onNoteCancelled(): void {
    this.tableState.onNoteCancelled();
  }

  /**
   * Az adott nap összes plan értékét 0-ra állítja és menti az adatbázisba (CC + KÜM módban egyaránt).
   * A "Tervszámok törlése" checkbox bepipálva + mentés esetén hívódik meg.
   */
  onClearPlanForDay(dayIndex: number): void {
    if (!this.canEditPlan || this.isSummaryTable) return;

    const actualYear = this.getEffectiveYear();
    const date = new Date(actualYear, this.getMonthNumber() - 1, dayIndex + 1);
    const weekNumber = this.dateService.getWeekNumber(date);

    // Összes plan metric nullázása az adott napra (lokálisan)
    this.metrics.forEach((_, metricIndex) => {
      this.metrics[metricIndex].planValues[dayIndex] = 0;
      this.updatePercentages(metricIndex);
    });
    this.cdr.detectChanges();

    // Mentés az adatbázisba a meglévő save metódusokon keresztül
    if (this.isKumMode) {
      const metricIndices = this.metrics.map((_, i) => i);
      this.saveKumPlanValues(weekNumber, actualYear, metricIndices, [dayIndex]);
    } else {
      this.saveCallCenterPlanValues(weekNumber, actualYear, [dayIndex]);
    }
  }

  /**
   * A nap munkanapo-e (hétfő-péntek)
   */
  isWeekday(dayIndex: number): boolean {
    return this.dateService.isWeekday(dayIndex, this.year, this.getMonthIndex(), this.daysInMonth);
  }

  /**
   * Jegyzetek mentése az adatbázisba (NotesManagerService delegálás)
   */
  private saveNotesToDatabase(changedDayIndex?: number, isDelete = false): void {
    this.notesManager.saveNotesToDatabase(
      this.userId,
      this.year,
      this.daysInMonth,
      this.isKumMode,
      () => this.getMonthNumber(),
      () => this.getMonthIndex(),
      (date: Date) => this.dateService.getWeekNumber(date),
      (dayIndex: number) => this.isWeekday(dayIndex),
      changedDayIndex,
      isDelete
    );
  }

  /**
   * Jegyzetek betöltése előre betöltött (bulk) adatokból, API hívás nélkül.
   * Üres preloaded tömb = nincs jegyzet, NEM indít egyedi API hívásokat.
   */
  private loadNotesFromPreloadedStats(): void {
    if (this.preloadedStats && this.preloadedStats.length > 0) {
      const effectiveYear = this.getEffectiveYear();
      this.notesManager.loadNotesFromPreloadedStats(
        this.preloadedStats,
        effectiveYear,
        this.daysInMonth,
        () => this.getMonthIndex(),
        this.cdr
      );
    }
    // Üres preloaded: nincs adat → nincs jegyzet, nem kell API hívás
  }

  /**
   * Jegyzetek betöltése előre betöltött KÜM (bulk) adatokból, API hívás nélkül.
   * Üres preloaded tömb = nincs jegyzet, NEM indít egyedi API hívásokat.
   */
  private loadNotesFromPreloadedKumStats(): void {
    if (this.preloadedKumStats && this.preloadedKumStats.length > 0) {
      const effectiveYear = this.getEffectiveYear();
      this.notesManager.loadNotesFromPreloadedKumStats(
        this.preloadedKumStats,
        effectiveYear,
        this.daysInMonth,
        () => this.getMonthIndex(),
        this.cdr
      );
    }
    // Üres preloaded: nincs adat → nincs jegyzet, nem kell API hívás
  }

  /**
   * Jegyzetek betöltése az adatbázisból (NotesManagerService delegálás)
   */
  private loadNotesFromDatabase(): void {
    this.notesManager.loadNotesFromDatabase(
      this.userId,
      this.year,
      this.month,
      this.daysInMonth,
      this.isKumMode,
      () => this.getMonthNumber(),
      () => this.getMonthIndex(),
      this.cdr
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Összesítő táblázat napi notejai (summary_notes tábla)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Az összesítő táblázat notesait tölti be az adatbázisból.
   */
  private loadSummaryNotes(): void {
    const monthNumber = this.getMonthNumber();
    if (monthNumber === -1) return;
    this.summaryNotesService.getNotes(this.year, monthNumber, this.isKumMode).subscribe({
      next: (notes) => {
        const map: { [dayIndex: number]: string } = {};
        notes.forEach(n => { map[n.day_index] = n.note; });
        this.summaryNotes = map;
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  /** Van-e összesítő note az adott naphoz */
  hasSummaryNoteForDay(dayIndex: number): boolean {
    return !!(this.summaryNotes[dayIndex] && this.summaryNotes[dayIndex].trim() !== '');
  }

  /** Visszaadja az összesítő note szövegét */
  getSummaryNoteForDay(dayIndex: number): string {
    return this.summaryNotes[dayIndex] || '';
  }

  /** Szerkesztési ikon megjelenítése: csak hoverre, csak ha nincs még note, csak canEditPlan esetén */
  shouldShowSummaryNoteIcon(dayIndex: number): boolean {
    if (this.isWeekend(dayIndex) || !this.canEditPlan) return false;
    return this.tableState.currentHoveredDay === dayIndex && !this.hasSummaryNoteForDay(dayIndex);
  }

  /** Chip megjelenítése: ha van note (mindenki látja) */
  shouldShowSummaryNoteChip(dayIndex: number): boolean {
    return this.hasSummaryNoteForDay(dayIndex);
  }

  /**
   * Összesítő note mentése / módosítása
   */
  onSummaryNoteAdded(note: string, dayIndex: number): void {
    const monthNumber = this.getMonthNumber();
    if (monthNumber === -1) return;
    this.summaryNotesService.upsertNote({
      year: this.year,
      month: monthNumber,
      day_index: dayIndex,
      is_kum_mode: this.isKumMode,
      note,
    }).subscribe({
      next: (saved) => {
        this.summaryNotes = { ...this.summaryNotes, [dayIndex]: saved.note };
        this.cdr.detectChanges();
        this.snackBar.open('✅ Összesítő jegyzet sikeresen mentve!', 'Bezár', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
      },
      error: () => {
        this.snackBar.open('❌ Hiba az összesítő jegyzet mentésekor!', 'Bezár', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
    this.onMenuAction();
  }

  /**
   * Összesítő note törlése
   */
  onSummaryNoteDeleted(dayIndex: number): void {
    const monthNumber = this.getMonthNumber();
    if (monthNumber === -1) return;
    this.summaryNotesService.deleteNote(this.year, monthNumber, dayIndex, this.isKumMode).subscribe({
      next: () => {
        const updated = { ...this.summaryNotes };
        delete updated[dayIndex];
        this.summaryNotes = updated;
        this.cdr.detectChanges();
        this.snackBar.open('✅ Összesítő jegyzet sikeresen törölve!', 'Bezár', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
      },
      error: () => {
        this.snackBar.open('❌ Hiba az összesítő jegyzet törlésekor!', 'Bezár', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
    this.onMenuAction();
  }

  // #endregion

  /** Módosítási napló dialog megnyitása az adott felhasználóhoz és hónaphoz */
  openHistoryDialog(event: MouseEvent): void {
    // Ha már nyitva van, ne nyissa meg újra
    if (this.historyDialogRef) {
      return;
    }

    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dialogWidth = 420;

    // Az ikon jobb oldalára pozicionálunk, ha elfér, különben balra
    let left = rect.right + 8;
    if (left + dialogWidth > viewportWidth - 16) {
      left = rect.left - dialogWidth - 8;
    }
    // Vertikálisan az ikon közepéhez igazítva
    const top = Math.max(8, rect.top - 40);

    // Fókusz elvétele a szerkesztési inputtól, hogy az aria-hidden overlay conflict ne lépjen fel
    (document.activeElement as HTMLElement)?.blur();

    this.historyDialogRef = this.dialog.open(StatsHistoryDialogComponent, {
      width: `${dialogWidth}px`,
      maxWidth: '92vw',
      maxHeight: '70vh',
      position: {
        top: `${top}px`,
        left: `${left}px`
      },
      hasBackdrop: false,
      disableClose: true,
      panelClass: 'stats-history-panel',
      data: {
        userId: this.userId,
        colleagueName: this.colleagueName,
        month: this.month,
        year: this.getEffectiveYear()
      }
    });

    this.historyDialogRef.afterClosed().subscribe(() => {
      this.historyDialogRef = null;
    });
  }
}